import { executeAthenaRequest } from "./executor.ts";
import type { AthenaRuntimeExecutionEvent, AthenaServerRuntime } from "./types.ts";

/** Browser DevTools sets this on `/api/athena` while the overlay is open. */
export const ATHENA_DEVTOOLS_REQUEST_HEADER = "x-athena-devtools";

/** Compact sanitized Data Nucleus summaries. Never a row dump. */
export const ATHENA_DEVTOOLS_DATA_HEADER = "x-athena-data-nucleus";

export const ATHENA_DEVTOOLS_TRACE_HEADER = "x-athena-trace-id";

export type AthenaDevtoolsDataEvent = {
	affectedRows: number | null;
	errorPhase: string | null;
	event: string;
	eventId: string | null;
	operation: string;
	policyIds: string[] | null;
	policyOutcome: string | null;
	principal: {
		authority: string | null;
	};
	requestId: string | null;
	resource: string | null;
	timings: {
		afterHooksMs: number | null;
		authorizeMs: number | null;
		beforeHooksMs: number | null;
		executeMs: number | null;
		prepareMs: number | null;
		totalMs: number | null;
	};
	traceId: string | null;
	transactionSemantics: "atomic" | "backend-managed" | "unknown" | null;
};

function nullableNumber(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function nullableString(value: unknown): string | null {
	return typeof value === "string" && value.trim() ? value.trim() : null;
}

function nucleusEventName(event: AthenaRuntimeExecutionEvent): string {
	const semantic = event.semanticOperation;
	if (
		semantic === "insert" ||
		semantic === "update" ||
		semantic === "delete" ||
		semantic === "upsert"
	) {
		return `data.${semantic}`;
	}
	if (
		event.operation === "insert" ||
		event.operation === "update" ||
		event.operation === "delete"
	) {
		return `data.${event.operation}`;
	}
	return event.operation;
}

function transactionSemantics(
	value: AthenaRuntimeExecutionEvent["transactionSemantics"],
): AthenaDevtoolsDataEvent["transactionSemantics"] {
	if (
		value === "atomic" ||
		value === "backend-managed" ||
		value === "unknown"
	) {
		return value;
	}
	return null;
}

/** Allowlisted projection — no payload, records, or secrets. */
export function toAthenaDevtoolsDataEvent(
	event: AthenaRuntimeExecutionEvent,
): AthenaDevtoolsDataEvent {
	return {
		affectedRows: nullableNumber(event.affectedRows),
		errorPhase: nullableString(event.errorPhase),
		event: nucleusEventName(event),
		eventId: nullableString(event.eventId),
		operation: event.operation,
		policyIds: Array.isArray(event.policyIds)
			? event.policyIds.filter(
					(id): id is string => typeof id === "string" && Boolean(id.trim()),
				)
			: null,
		policyOutcome: nullableString(event.decision),
		principal: {
			authority: nullableString(event.principalAuthority),
		},
		requestId: nullableString(event.requestId),
		resource: nullableString(event.resource),
		timings: {
			afterHooksMs: nullableNumber(event.afterHooksMs),
			authorizeMs: nullableNumber(event.authorizeMs),
			beforeHooksMs: nullableNumber(event.beforeHooksMs),
			executeMs: nullableNumber(event.executeMs),
			prepareMs: nullableNumber(event.prepareMs),
			totalMs: nullableNumber(event.totalMs),
		},
		traceId: nullableString(event.traceId),
		transactionSemantics: transactionSemantics(event.transactionSemantics),
	};
}

export function requestWantsAthenaDevtools(request: Request): boolean {
	return request.headers.get(ATHENA_DEVTOOLS_REQUEST_HEADER)?.trim() === "1";
}

export function wrapRuntimeForAthenaDevtools(
	runtime: AthenaServerRuntime,
	sink: AthenaDevtoolsDataEvent[],
): AthenaServerRuntime {
	const previous = runtime.onExecutionEvent;
	const wrapped: AthenaServerRuntime = {
		...runtime,
		execute(request, context) {
			return executeAthenaRequest(wrapped, request, context);
		},
		onExecutionEvent(event) {
			previous?.(event);
			sink.push(toAthenaDevtoolsDataEvent(event));
		},
	};
	return wrapped;
}

export function attachAthenaDevtoolsDataHeader(
	response: Response,
	events: readonly AthenaDevtoolsDataEvent[],
	traceId?: string | null,
): Response {
	const headers = new Headers(response.headers);
	headers.set(
		ATHENA_DEVTOOLS_DATA_HEADER,
		encodeURIComponent(JSON.stringify(events)),
	);
	const resolvedTrace = traceId?.trim() || events[0]?.traceId;
	if (resolvedTrace) {
		headers.set(ATHENA_DEVTOOLS_TRACE_HEADER, resolvedTrace);
	}
	const extra = [
		ATHENA_DEVTOOLS_DATA_HEADER,
		ATHENA_DEVTOOLS_TRACE_HEADER,
		"x-athena-request-id",
		"x-request-id",
	].join(", ");
	const exposed = headers.get("Access-Control-Expose-Headers");
	headers.set(
		"Access-Control-Expose-Headers",
		exposed ? `${exposed}, ${extra}` : extra,
	);
	return new Response(response.body, {
		headers,
		status: response.status,
		statusText: response.statusText,
	});
}
